import { useCallback, useState } from "react";
import { v4 as UUID } from "uuid";
import { t } from "@/i18n-js/instance";
import { FadingTextEffect } from "@/react/components/shared/FadingTextEffect";
import { LabelSm } from "@/react/components/shared/uikit/Typography/components/Label/LabelSm";
import { formattedDateTime } from "@/react/helpers/dateTimeHelpers/formattedDateTime";
import { wrapAsHtmlFence } from "@/react/helpers/htmlContent";
import { Icon } from "@circle-react/components/shared/Icon";
import { usePunditUserContext } from "@circle-react/contexts";
import { TippyV2 } from "@circle-react-shared/TippyV2";
import { SanitizeHTMLString } from "@circle-react-shared/uikit/SanitizeContent";
import { Typography } from "@circle-react-shared/uikit/Typography";
import type { ChatButtonProps } from "../../types/ChatButtonProps";
import type { ChatCardProps } from "../../types/ChatCard";
import type { ConversationChannel } from "../../types/ConversationChannel";
import type { Attachment } from "../../types/attachment";
import { AttachmentsList } from "./AttachmentsList";
import { BotAvatar } from "./BotAvatar";
import { ChatButton } from "./ChatButton";
import { ChatCard } from "./ChatCard";
import { SlideInAnimation } from "./SlideInAnimation";
import { UserAvatar } from "./UserAvatar";
import "../../assets/styles/chat-message.css";

const ALLOWED_HTML_TAGS = [
  "a",
  "br",
  "em",
  "i",
  "li",
  "ol",
  "p",
  "strong",
  "ul",
  "b",
] as const;

export interface ChatMessageProps {
  animateUiElements?: boolean;
  attachments?: Attachment[];
  authorAvatarUrl?: string;
  authorName?: string;
  buttons: Omit<ChatButtonProps, "selected" | "setSelected">[];
  cards?: ChatCardProps[];
  channel: ConversationChannel;
  content: string;
  createdAt: string;
  onRevealComplete?: () => void;
  selectedButton: string | null;
  shouldShowAvatar?: boolean;
  showDisclaimer: boolean;
  showFadingTextEffect?: boolean;
  type: "business" | "bot" | "user" | "system";
}

export const ChatMessage = ({
  type,
  authorAvatarUrl,
  authorName,
  content,
  createdAt,
  channel,
  buttons,
  cards = [],
  selectedButton,
  attachments = [],
  shouldShowAvatar = true,
  showDisclaimer = false,
  showFadingTextEffect = false,
  animateUiElements = false,
  onRevealComplete,
}: ChatMessageProps) => {
  const [selected, setSelected] = useState<string | null>(selectedButton);
  const [isRevealComplete, setIsRevealComplete] =
    useState(!showFadingTextEffect);
  const {
    currentCommunityMember,
    currentCommunitySettings,
    isLoading: isLoadingPunditUserContext,
  } = usePunditUserContext();

  const handleOnComplete = useCallback(() => {
    setIsRevealComplete(true);
    onRevealComplete?.();
  }, [onRevealComplete, setIsRevealComplete]);

  const isCopilotActionsEnabled =
    !isLoadingPunditUserContext &&
    currentCommunitySettings?.help_widget_copilot_actions_enabled;
  const authorMapping = {
    business: {
      avatarUrl: authorAvatarUrl ?? "",
      name: authorName ?? "",
    },
    bot: {
      avatarUrl: "",
      name: isCopilotActionsEnabled
        ? t("help_widget.copilot.display_name")
        : t("support_widget.chatbot.name"),
    },
    user: {
      avatarUrl: currentCommunityMember?.avatar_url ?? "",
      name: currentCommunityMember?.name ?? "",
    },
    system: {
      avatarUrl: "",
      name: "Agent",
    },
  };

  const formattedCreatedAt = formattedDateTime({
    dateTime: new Date(createdAt),
    format: "short_time",
  });

  const shouldShowCreatedAt = channel === "live_chat" || channel === "email";

  const chatButtons = buttons.map(buttonProps => {
    const { event } = buttonProps;

    return (
      <ChatButton
        key={event}
        {...buttonProps}
        selected={selected}
        setSelected={setSelected}
      />
    );
  });

  const chatCards = cards.map(cardProps => (
    <ChatCard key={UUID()} {...cardProps} />
  ));

  if (type === "user") {
    return (
      <div className="message-content flex flex-col items-end">
        {content?.trim() && (
          <div className="bg-circle/10 ml-6 flex w-fit flex-col gap-1 rounded-2xl rounded-br-[4px] px-4 py-3">
            <Typography.BodySm color="text-darkest">
              <SanitizeHTMLString
                content={content}
                sanitizeProps={{
                  ALLOWED_TAGS: ALLOWED_HTML_TAGS,
                  ADD_ATTR: ["target"],
                }}
              />
            </Typography.BodySm>
            {shouldShowCreatedAt && (
              <Typography.BodyXs color="text-disabled">
                {formattedCreatedAt}
              </Typography.BodyXs>
            )}
          </div>
        )}
        {attachments.length > 0 && (
          <div className="mt-4">
            <AttachmentsList attachments={attachments} />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-row items-start">
      <div className="flex flex-col">
        {shouldShowAvatar && (
          <div className="mb-2 flex flex-row items-center gap-2">
            {type === "bot" ? (
              <BotAvatar />
            ) : (
              <UserAvatar
                name={authorMapping[type]["name"]}
                src={authorMapping[type]["avatarUrl"]}
              />
            )}
            <div className="flex flex-row items-center gap-2">
              <LabelSm weight="semibold" color="text-dark">
                {authorMapping[type]["name"]}
              </LabelSm>
              {showDisclaimer && (
                <TippyV2
                  maxWidth={250}
                  className="mt-[-4px]"
                  content={
                    <SanitizeHTMLString
                      content={t("support_widget.chatbot.disclaimer")}
                    />
                  }
                >
                  <Icon type="16-info" size={16} />
                </TippyV2>
              )}
            </div>
          </div>
        )}
        <div className="message-content pr-4">
          {content?.trim() && (
            <div className="bg-secondary flex w-fit flex-col gap-1 rounded-2xl px-4 py-3">
              <Typography.BodySm color="text-darkest">
                <FadingTextEffect
                  text={wrapAsHtmlFence(content)}
                  active={showFadingTextEffect}
                  onComplete={handleOnComplete}
                  updateInterval={50}
                />
              </Typography.BodySm>
              {shouldShowCreatedAt && (
                <Typography.BodyXs color="text-disabled">
                  {formattedCreatedAt}
                </Typography.BodyXs>
              )}
            </div>
          )}
          {isRevealComplete && (
            <SlideInAnimation shouldAnimate={animateUiElements}>
              {attachments.length > 0 && (
                <div className="mt-4">
                  <AttachmentsList attachments={attachments} />
                </div>
              )}
              {chatButtons.length > 0 && (
                <div className="mt-3 flex flex-row flex-wrap gap-2">
                  {chatButtons}
                </div>
              )}
              {chatCards.length > 0 && (
                <div className="flex flex-row flex-wrap gap-2">{chatCards}</div>
              )}
            </SlideInAnimation>
          )}
        </div>
      </div>
    </div>
  );
};
